/**
 * Kalıcı silme onayı.
 *
 * Kütüphane silme modalından farkı: burada **disk dosyaları** silinir.
 * Bu yüzden mesaj, kapsam (dizi/sezon/bölüm/parça) ve geri alınamazlık
 * açıkça yazılır; rclone bağlantısının yazma gereksinimi belirtilir.
 */
import { type ReactNode, useRef } from 'react';
import type { Item } from '../api.ts';
import { displayTitle } from '../format.ts';
import { AlertIcon, InfoIcon } from '../Icon.tsx';
import { useModalFocus } from '../useModalFocus.ts';
import { ErrorNotice } from './States.tsx';

function scopeText(kind: Item['kind']): string {
  switch (kind) {
    case 'show':
      return 'Bu dizinin tüm sezon ve bölüm dosyaları';
    case 'season':
      return 'Bu sezonun tüm bölüm dosyaları';
    default:
      return 'Bu dosya';
  }
}

export function DeleteItemDialog({
  item,
  busy,
  error,
  onConfirm,
  onCancel,
}: {
  item: Item;
  busy: boolean;
  error: string | null;
  onConfirm: () => void;
  onCancel: () => void;
}): ReactNode {
  const title = displayTitle(item);
  const dialogRef = useRef<HTMLDivElement>(null);
  useModalFocus(dialogRef, onCancel);

  return (
    <div className="modal-backdrop">
      <button
        type="button"
        style={{
          position: 'absolute',
          inset: 0,
          background: 'transparent',
          border: 'none',
          cursor: 'pointer',
          width: '100%',
          height: '100%',
        }}
        onClick={() => {
          if (!busy) onCancel();
        }}
        aria-label="Kapat"
        tabIndex={-1}
      />
      <div
        ref={dialogRef}
        className="card modal"
        role="alertdialog"
        aria-labelledby="delete-item-title"
        aria-modal="true"
        tabIndex={-1}
        style={{
          position: 'relative',
          zIndex: 2,
          maxWidth: '480px',
          padding: '28px',
          background: 'var(--bg-elevated)',
          border: '1px solid var(--border)',
          borderRadius: '16px',
          boxShadow: '0 20px 50px rgba(0,0,0,0.6)',
        }}
      >
        <h2
          id="delete-item-title"
          className="card__title"
          style={{
            color: 'var(--danger)',
            display: 'flex',
            alignItems: 'center',
            gap: '10px',
            margin: '0 0 12px',
            fontSize: '1.25rem',
          }}
        >
          <AlertIcon size={20} />
          <span>Diskten Kalıcı Olarak Sil</span>
        </h2>

        <p
          style={{
            margin: '0 0 10px',
            fontSize: '0.98rem',
            color: 'var(--text)',
            lineHeight: 1.5,
          }}
        >
          <strong>“{title}”</strong> kalıcı olarak diskten silinecek. {scopeText(item.kind)}{' '}
          silinecek; bu işlem geri alınamaz.
        </p>

        <p
          style={{
            margin: '0 0 20px',
            fontSize: '0.85rem',
            color: 'var(--text-muted)',
            lineHeight: 1.4,
          }}
        >
          <InfoIcon size={15} />
          <strong>Not:</strong> rclone ile bağlı uzak kütüphanelerde dosyaların silinebilmesi için
          bağlantının yazma izniyle açık olması gerekir. Silme başarısız olursa katalog kayıtları
          korunur.
        </p>

        {error && (
          <div style={{ marginBottom: '16px' }}>
            <ErrorNotice message={error} />
          </div>
        )}

        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '12px' }}>
          <button type="button" className="button button--ghost" onClick={onCancel} disabled={busy}>
            Vazgeç
          </button>
          <button
            type="button"
            className="button"
            style={{
              background: 'var(--danger)',
              color: '#ffffff',
              border: 'none',
              padding: '10px 18px',
              fontWeight: 600,
            }}
            onClick={onConfirm}
            disabled={busy}
          >
            {busy ? 'Siliniyor…' : 'Evet, Diskten Sil'}
          </button>
        </div>
      </div>
    </div>
  );
}
